// @ts-check
// Aba Configurações do editor (contrato de bots, seções 2.2, 12 e 22.2): finalidade do bot, prioridade e descrição
// (gravadas na hora pelo PATCH), espera por pergunta, lembrete, textos padrão (lembrete, sem resposta, resposta
// inválida), tentativas, "Falar com uma pessoa" (o texto da opção e para onde vai), as palavras globais ("menu" volta ao
// começo, "atendente" chama uma pessoa) e o silêncio depois que a equipe responde.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { SegmentedControl } from '../../ui/segmented-control.js';
import { Toggle } from '../../ui/feedback.js';
import { parseBotKeywords } from '../../lib/bot-editor-state.js';
import {
  MAXIMUM_BOT_DESCRIPTION_LENGTH, MAXIMUM_BOT_MUTE_HOURS, MAXIMUM_BOT_PRIORITY, MAXIMUM_BOT_QUESTION_TIMEOUT_MINUTES, MAXIMUM_BOT_RETRIES,
  MINIMUM_BOT_MUTE_HOURS, MINIMUM_BOT_PRIORITY, MINIMUM_BOT_QUESTION_TIMEOUT_MINUTES, MINIMUM_BOT_RETRIES,
} from '../../../../shared/bots/bot-limits.js';
import { BOT_PURPOSES } from '../../../../shared/bots/bot-definition.js';
import { FlowSidePanel } from '../flows/flow-side-panel.js';
import { FlowField, FlowNotice } from '../flows/flow-ui.js';
import { BotNumberField, BotTextField, useFieldId } from './bot-form-fields.js';
import { HandoffTargetFields } from './node-forms/service-forms.js';
import { botProblemText } from './bot-ui.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/bots/bot-definition.js').BotDefinition} BotDefinition
 * @typedef {import('../../lib/bot-validation.js').BotEditorProblem} BotEditorProblem
 * @typedef {import('./bot-lookups.js').BotLookupData} BotLookupData
 */

/**
 * Lista de palavras globais (vírgulas), gravada ao sair do campo.
 * @param {{ label: string, hint: string, words: string[], onChange: (words: string[]) => void }} props
 */
function GlobalWordsField({ label, hint, words, onChange }) {
  const id = useFieldId('bot-global-words');
  return html`<${FlowField} label=${label} htmlFor=${id} hint=${hint}>
    <input id=${id} class="text-input" value=${words.map((word) => word.toLowerCase()).join(', ')}
      onChange=${(/** @type {Event} */ event) => onChange(parseBotKeywords(/** @type {HTMLInputElement} */ (event.currentTarget).value))} />
  <//>`;
}

/**
 * @param {{ definition: BotDefinition, priority: number, description: string | null, lookupData: BotLookupData, problems: BotEditorProblem[],
 *   onChange: (definition: BotDefinition) => void, onDetailsChange: (patch: { priority?: number, description?: string | null }) => void, onClose: () => void }} props
 */
export function BotSettingsPanel({ definition, priority, description, lookupData, problems, onChange, onDetailsChange, onClose }) {
  const settings = definition.settings;
  const [draftDescription, setDraftDescription] = useState(description ?? '');
  const [draftPriority, setDraftPriority] = useState(/** @type {number | null} */ (priority));
  useEffect(() => setDraftDescription(description ?? ''), [description]);
  useEffect(() => setDraftPriority(priority), [priority]);
  /** @param {Partial<BotDefinition['settings']>} patch */
  const set = (patch) => onChange({ ...definition, settings: { ...settings, ...patch } });
  // o campo vem como o motor grava (`settings.questionTimeoutMinutes`, `settings.humanHandoff.teamId`); aqui sem o prefixo
  const settingsProblems = problems.filter((problem) => problem.area === 'SETTINGS').map((problem) => (String(problem.details?.field ?? '').startsWith('settings.')
    ? { ...problem, details: { ...problem.details, field: String(problem.details?.field).slice('settings.'.length) } } : problem));
  /** @param {string} field */
  const errorOf = (field) => {
    const problem = settingsProblems.find((candidate) => candidate.details?.field === field || String(candidate.details?.field ?? '').startsWith(`${field}.`));
    return problem ? botProblemText(problem) : null;
  };
  const noHumanPath = settingsProblems.find((problem) => problem.code === 'NO_HUMAN_PATH');
  const hasReminder = settings.reminderAfterMinutes !== null && settings.reminderAfterMinutes !== undefined;
  const descriptionId = useFieldId('bot-description');
  return html`<${FlowSidePanel} title=${t('bots.settings.title')} help=${t('bots.settings.help')} onClose=${onClose} panelKey="settings" testId="bot-settings-panel"
    backLabel=${t('bots.panel.backToBot')} footer=${html`<${Button} title=${t('bots.panel.close')} variant="secondary" size="small" onClick=${onClose} />`}>
    <section class="bot-settings-section">
      <h3 class="flow-panel-section-title">${t('bots.settings.general')}</h3>
      <div class="flow-field">
        <span class="flow-field-label">${t('bots.settings.purpose')}</span>
        <${SegmentedControl} label=${t('bots.settings.purpose')} value=${definition.purpose}
          options=${BOT_PURPOSES.map((value) => ({ value, label: t(`bots.purposes.${value}`) }))} onChange=${(/** @type {any} */ purpose) => onChange({ ...definition, purpose })} />
        <span class="flow-field-hint">${t(`bots.purposeHints.${definition.purpose}`)}</span>
      </div>
      <${BotNumberField} label=${t('bots.settings.priority')} value=${draftPriority} minimum=${MINIMUM_BOT_PRIORITY} maximum=${MAXIMUM_BOT_PRIORITY}
        hint=${t('bots.settings.priorityHint')} onChange=${(value) => {
          setDraftPriority(value);
          if (value !== null && value >= MINIMUM_BOT_PRIORITY && value <= MAXIMUM_BOT_PRIORITY && value !== priority) onDetailsChange({ priority: value });
        }} />
      <${FlowField} label=${t('bots.settings.description')} htmlFor=${descriptionId}>
        <textarea id=${descriptionId} class="text-input bot-textarea" rows="2" maxLength=${MAXIMUM_BOT_DESCRIPTION_LENGTH} value=${draftDescription}
          onInput=${(/** @type {InputEvent} */ event) => setDraftDescription(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}
          onBlur=${() => (draftDescription.trim() || null) !== (description ?? null) && onDetailsChange({ description: draftDescription.trim() || null })}></textarea>
      <//>
    </section>
    <section class="bot-settings-section">
      <h3 class="flow-panel-section-title">${t('bots.settings.waiting')}</h3>
      <${BotNumberField} label=${t('bots.settings.questionTimeout')} value=${settings.questionTimeoutMinutes} minimum=${MINIMUM_BOT_QUESTION_TIMEOUT_MINUTES}
        maximum=${MAXIMUM_BOT_QUESTION_TIMEOUT_MINUTES} suffix=${t('bots.forms.minutesSuffix')} error=${errorOf('questionTimeoutMinutes')}
        onChange=${(questionTimeoutMinutes) => set({ questionTimeoutMinutes: questionTimeoutMinutes ?? 0 })} />
      <label class="flow-checkbox-row bot-toggle-row">
        <${Toggle} value=${hasReminder} ariaLabel=${t('bots.settings.reminder')} onChange=${(/** @type {boolean} */ value) => set({ reminderAfterMinutes: value ? 15 : null })} />
        <span>${t('bots.settings.reminder')}</span>
      </label>
      ${hasReminder
        ? html`<${BotNumberField} label=${t('bots.settings.reminderAfter')} value=${settings.reminderAfterMinutes} minimum=${1} suffix=${t('bots.forms.minutesSuffix')}
            error=${errorOf('reminderAfterMinutes')} onChange=${(reminderAfterMinutes) => set({ reminderAfterMinutes: reminderAfterMinutes ?? 1 })} />
          <${BotTextField} label=${t('bots.settings.reminderText')} value=${settings.reminderText} onChange=${(reminderText) => set({ reminderText: reminderText || null })} />`
        : null}
      <${BotTextField} label=${t('bots.settings.timeoutText')} value=${settings.timeoutText} hint=${t('bots.settings.timeoutTextHint')}
        onChange=${(timeoutText) => set({ timeoutText: timeoutText || null })} />
      <${BotTextField} label=${t('bots.settings.invalidText')} value=${settings.invalidText} onChange=${(invalidText) => set({ invalidText: invalidText || null })} />
      <${BotNumberField} label=${t('bots.settings.retries')} value=${settings.retries} minimum=${MINIMUM_BOT_RETRIES} maximum=${MAXIMUM_BOT_RETRIES}
        hint=${t('bots.settings.retriesHint')} onChange=${(retries) => set({ retries: Math.max(MINIMUM_BOT_RETRIES, Math.min(MAXIMUM_BOT_RETRIES, retries ?? 0)) })} />
    </section>
    <section class="bot-settings-section">
      <h3 class="flow-panel-section-title">${t('bots.settings.human')}</h3>
      ${noHumanPath ? html`<${FlowNotice} tone="warning" icon="IconAlertTriangle">${botProblemText(noHumanPath)}<//>` : null}
      <${BotTextField} label=${t('bots.settings.humanOptionLabel')} value=${settings.humanOptionLabel} maximum=${20}
        onChange=${(humanOptionLabel) => set({ humanOptionLabel })} />
      <label class="flow-checkbox-row bot-toggle-row">
        <${Toggle} value=${Boolean(settings.humanHandoff)} ariaLabel=${t('bots.settings.humanHandoff')}
          onChange=${(/** @type {boolean} */ value) => set({ humanHandoff: value ? { kind: 'TEAM', teamId: /** @type {any} */ (null) } : null })} />
        <span>${t('bots.settings.humanHandoff')}</span>
      </label>
      ${settings.humanHandoff
        ? html`<${HandoffTargetFields} target=${settings.humanHandoff} lookupData=${lookupData} fieldPrefix="humanHandoff"
            problems=${settingsProblems.filter((problem) => String(problem.details?.field ?? '').startsWith('humanHandoff'))}
            onChange=${(/** @type {any} */ humanHandoff) => set({ humanHandoff })} />`
        : null}
      <${GlobalWordsField} label=${t('bots.settings.menuWords')} hint=${t('bots.settings.menuWordsHint')} words=${settings.globalWords?.menu ?? []}
        onChange=${(menu) => set({ globalWords: { ...settings.globalWords, menu } })} />
      <${GlobalWordsField} label=${t('bots.settings.humanWords')} hint=${t('bots.settings.humanWordsHint')} words=${settings.globalWords?.human ?? []}
        onChange=${(human) => set({ globalWords: { ...settings.globalWords, human } })} />
      <${BotNumberField} label=${t('bots.settings.mute')} value=${settings.muteHoursAfterHuman} minimum=${MINIMUM_BOT_MUTE_HOURS} maximum=${MAXIMUM_BOT_MUTE_HOURS}
        suffix=${t('bots.settings.hoursSuffix')} hint=${t('bots.settings.muteHint')}
        onChange=${(muteHoursAfterHuman) => set({ muteHoursAfterHuman: Math.max(MINIMUM_BOT_MUTE_HOURS, Math.min(MAXIMUM_BOT_MUTE_HOURS, muteHoursAfterHuman ?? MINIMUM_BOT_MUTE_HOURS)) })} />
      <p class="flow-field-hint">${t('bots.settings.stopWordsHint')}</p>
    </section>
  <//>`;
}
